/**
 * MessageFeedback · AI 消息底部点赞 / 点踩
 *
 * 接 /web/workgroup/folder/ai/chat/feedback · 1=点赞 / 2=点踩
 *
 * 交互：
 *  · 未评价：两个 icon 灰色 · hover 主色
 *  · 已点赞：thumbs-up 实心主色 · 再点取消（API 约定 feedback=1 重复发送，
 *    本期前端只切换本地视觉，不撤销；后续若后端支持 feedback=0 再加）
 *  · 已点踩：thumbs-down 实心红色
 *  · 同一时刻 like / dislike 互斥
 *  · in-flight 期间禁用避免重复请求
 *  · 没有 backendMessageId（极少数 streaming 还没收到 done 的消息）→ 不渲染
 */

import React, { useState } from 'react'
import { message as antdMessage, Tooltip } from 'antd'
import { DislikeOutlined, LikeOutlined } from '@ant-design/icons'
import classNames from 'classnames'
import { useAppDispatch, useAppSelector } from '../../../../../../app/hooks'
import {
  messageFeedbackSet,
  selectConversationId
} from '../../../../../../slices/aiClassify'
import { sendAiChatFeedback } from '../../../../services/aiChatFeedback'
import type { AssistantMessage } from '../../../../../../types/ai-classify'
import styles from './MessageAssistant.module.scss'

interface Props {
  message: AssistantMessage
}

const MessageFeedback: React.FC<Props> = ({ message }) => {
  const dispatch = useAppDispatch()
  const conversationId = useAppSelector(selectConversationId)
  const groupID = useAppSelector((s) => s.group.groupId)
  const [submitting, setSubmitting] = useState<0 | 1 | 2>(0)

  // 缺 backendMessageId · 历史消息或 SSE 未带都跳过（不渲染）
  if (!message.backendMessageId) return null
  if (!conversationId || !groupID) return null

  const current = message.feedback ?? 0

  const handleClick = async (next: 1 | 2): Promise<void> => {
    // 已是该状态 → 本期不做撤销（不发请求，也不闪状态）
    if (current === next) return
    if (submitting !== 0) return
    const prev = current
    // 乐观更新
    dispatch(messageFeedbackSet({ messageId: message.id, feedback: next }))
    setSubmitting(next)
    try {
      await sendAiChatFeedback({
        conversation_id: conversationId,
        group_id: groupID,
        message_id: message.backendMessageId as number,
        feedback: next
      })
    } catch (e) {
      // 失败回滚 · 给用户提示
      dispatch(messageFeedbackSet({ messageId: message.id, feedback: prev }))
      antdMessage.error((e as Error).message || '反馈提交失败,请稍后重试')
    } finally {
      setSubmitting(0)
    }
  }

  return (
    <div
      className={classNames(styles.feedback, {
        [styles.feedbackPinned]: current !== 0
      })}
      role="group"
      aria-label="消息反馈"
    >
      <Tooltip title={current === 1 ? '已点赞' : '点赞'} placement="top">
        <button
          type="button"
          className={classNames(styles.feedbackBtn, {
            [styles.feedbackBtnActive]: current === 1,
            [styles.feedbackBtnLike]: current === 1,
            [styles.feedbackBtnSubmitting]: submitting !== 0
          })}
          onClick={() => handleClick(1)}
          onMouseDown={(e) => e.preventDefault()}
          aria-pressed={current === 1}
          aria-label="点赞"
        >
          <LikeOutlined />
        </button>
      </Tooltip>
      <Tooltip title={current === 2 ? '已点踩' : '点踩'} placement="top">
        <button
          type="button"
          className={classNames(styles.feedbackBtn, {
            [styles.feedbackBtnActive]: current === 2,
            [styles.feedbackBtnDislike]: current === 2,
            [styles.feedbackBtnSubmitting]: submitting !== 0
          })}
          onClick={() => handleClick(2)}
          onMouseDown={(e) => e.preventDefault()}
          aria-pressed={current === 2}
          aria-label="点踩"
        >
          <DislikeOutlined />
        </button>
      </Tooltip>
    </div>
  )
}

export default MessageFeedback
